/**
 * Контракты ценообразования (E12, ФТ-10.2–10.6).
 * Глобальный коэффициент, персональная надбавка, базовые цены узлов.
 */

import { z } from "zod";
import { decimalStringSchema } from "./catalog";
import { apiSuccessSchema } from "./envelope";

/** Ключ глобального коэффициента в `price_settings`. */
export const GLOBAL_PRICE_COEFFICIENT_KEY = "global" as const;

/** Глобальный коэффициент / цена за кг > 0 (ФТ-10.2–10.3). */
export const positiveDecimalStringSchema = decimalStringSchema.refine(
  (value) => Number(value) > 0,
  { message: "Ожидается положительное десятичное число" },
);

export const globalPriceCoefficientSchema = z.object({
  key: z.literal(GLOBAL_PRICE_COEFFICIENT_KEY),
  coefficient: positiveDecimalStringSchema,
  updatedAt: z.string().datetime().nullable(),
});
export type GlobalPriceCoefficient = z.infer<typeof globalPriceCoefficientSchema>;

export const updateGlobalPriceCoefficientRequestSchema = z.object({
  coefficient: positiveDecimalStringSchema,
});
export type UpdateGlobalPriceCoefficientRequest = z.infer<
  typeof updateGlobalPriceCoefficientRequestSchema
>;

export const globalPriceCoefficientResponseSchema = apiSuccessSchema(globalPriceCoefficientSchema);

/** Персональная надбавка в процентах (ФТ-10.5): 10 → множитель (1 + 10/100). */
export const updateClientMarkupRequestSchema = z.object({
  priceMarkup: decimalStringSchema,
});
export type UpdateClientMarkupRequest = z.infer<typeof updateClientMarkupRequestSchema>;

const componentCodeSchema = z.string().trim().min(1).max(128);
const componentNameSchema = z.string().trim().min(1).max(512);
const componentUnitSchema = z.string().trim().min(1).max(64);

/** Компонент с базовой ценой руб./кг (ФТ-10.2). */
export const pricingComponentSchema = z.object({
  id: z.string().uuid(),
  code: componentCodeSchema,
  name: componentNameSchema,
  unit: componentUnitSchema,
  pricePerKg: positiveDecimalStringSchema,
  /** Опциональная привязка цены к группе конвейеров; null — общая база. */
  groupId: z.string().uuid().nullable(),
  priceUpdatedAt: z.string().datetime(),
});
export type PricingComponent = z.infer<typeof pricingComponentSchema>;

export const createPricingComponentRequestSchema = z.object({
  code: componentCodeSchema,
  name: componentNameSchema,
  unit: componentUnitSchema.default("шт"),
  pricePerKg: positiveDecimalStringSchema,
  groupId: z.string().uuid().nullable().optional(),
});
export type CreatePricingComponentRequest = z.infer<typeof createPricingComponentRequestSchema>;

export const updatePricingComponentPriceRequestSchema = z.object({
  pricePerKg: positiveDecimalStringSchema,
  groupId: z.string().uuid().nullable().optional(),
});
export type UpdatePricingComponentPriceRequest = z.infer<
  typeof updatePricingComponentPriceRequestSchema
>;

export const pricingComponentListResponseSchema = apiSuccessSchema(z.array(pricingComponentSchema));
export const pricingComponentResponseSchema = apiSuccessSchema(pricingComponentSchema);

/** Строка расчёта цены по ведомости (ФТ-10.1). */
export const pricePreviewLineSchema = z.object({
  bomItemId: z.string().uuid(),
  section: z.string().min(1),
  componentCode: z.string().nullable(),
  name: z.string().nullable(),
  qty: decimalStringSchema,
  massKg: decimalStringSchema,
  weightSource: z.enum(["unit", "total"]),
  pricePerKg: positiveDecimalStringSchema,
  lineTotal: decimalStringSchema,
});
export type PricePreviewLine = z.infer<typeof pricePreviewLineSchema>;

export const pricePreviewSkippedSchema = z.object({
  bomItemId: z.string().uuid(),
  reason: z.enum(["requires_mapping", "missing_price", "missing_mass"]),
  componentCode: z.string().nullable(),
  name: z.string().nullable(),
});
export type PricePreviewSkipped = z.infer<typeof pricePreviewSkippedSchema>;

export const pricePreviewResultSchema = z.object({
  conveyorUnitId: z.string().uuid(),
  bomVersionId: z.string().uuid(),
  bomVersionNo: z.number().int().positive(),
  clientId: z.string().uuid().nullable(),
  subtotal: decimalStringSchema,
  globalCoefficient: positiveDecimalStringSchema,
  groupCoefficient: positiveDecimalStringSchema,
  /** Надбавка в %, как в `clients.price_markup`. */
  priceMarkupPercent: decimalStringSchema,
  total: decimalStringSchema,
  lines: z.array(pricePreviewLineSchema),
  skipped: z.array(pricePreviewSkippedSchema),
});
export type PricePreviewResult = z.infer<typeof pricePreviewResultSchema>;

export const pricePreviewResultResponseSchema = apiSuccessSchema(pricePreviewResultSchema);

export const PRICING_ERROR_CODES = {
  NOT_FOUND: "PRICING_NOT_FOUND",
  CODE_TAKEN: "PRICING_CODE_TAKEN",
  GROUP_NOT_FOUND: "PRICING_GROUP_NOT_FOUND",
  UNIT_NOT_FOUND: "PRICING_UNIT_NOT_FOUND",
  NO_PUBLISHED_BOM: "PRICING_NO_PUBLISHED_BOM",
  CLIENT_NOT_FOUND: "PRICING_CLIENT_NOT_FOUND",
} as const;
export type PricingErrorCode = (typeof PRICING_ERROR_CODES)[keyof typeof PRICING_ERROR_CODES];
